import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/contexts/AppContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowRight, ArrowLeft, Camera, Search, X, Check, Sparkles, PartyPopper } from "lucide-react";
import logoText from "@/assets/logo-text.png";

const CONTINENTS = ["Africa","Asia","Europe","North America","South America","Australia","Antarctica"];
const GENDERS = ["Male","Female","Other"];
const INTERESTS = [
  "Music","Gaming","Sports","Movies","Travel","Food","Fashion","Art","Tech","Fitness",
  "Books","Photography","Dancing","Cooking","Comedy","Anime","Cars","Nature","Pets","Business",
  "Yoga","Meditation","Hiking","Crypto","Investing","Writing","Singing","Football","Basketball","Tennis",
  "Painting","Drawing","DIY","Gardening","Coffee","Tea","Wine","Beer","Streaming","Podcasts",
  "Memes","Politics","Science","Space","History","Languages","Volunteering","Cycling","Running","Swimming",
  "Skating","Surfing","Skiing","Snowboarding","Boxing","MMA","Chess","Board Games","K-Pop","Hip-Hop",
  "Rock","Jazz","EDM","Classical","Country","R&B","Latin","Reggae","Theatre","Stand-up",
];

const TOTAL_STEPS = 6;

const SurveyPage = () => {
  const { completeSurvey, user, setUser } = useApp() as any;
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [username, setUsername] = useState(user?.username && !user.username.startsWith("user") ? user.username : "");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [continent, setContinent] = useState("");
  const [profilePic, setProfilePic] = useState<string>("");
  const [uploadingPic, setUploadingPic] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [interestSearch, setInterestSearch] = useState("");
  const [selectedInterests, setSelectedInterests] = useState<string[]>([]);
  const [customInterests, setCustomInterests] = useState<string[]>([]);

  const [sensitive, setSensitive] = useState(false);

  const next = () => setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  const allInterests = [...customInterests, ...INTERESTS.filter(i => !customInterests.includes(i))];
  const filteredInterests = allInterests.filter(i => i.toLowerCase().includes(interestSearch.toLowerCase()));
  const canAddCustom = interestSearch.trim().length > 1 && !allInterests.some(i => i.toLowerCase() === interestSearch.trim().toLowerCase());

  const toggleInterest = (i: string) => {
    setSelectedInterests((prev) => {
      if (prev.includes(i)) return prev.filter(x => x !== i);
      if (prev.length >= 10) { toast({ title: "Maximum reached", description: "You can pick up to 10 interests." }); return prev; }
      return [...prev, i];
    });
  };

  const addCustom = () => {
    const v = interestSearch.trim();
    if (!v || allInterests.some(i => i.toLowerCase() === v.toLowerCase())) return;
    if (selectedInterests.length >= 10) { toast({ title: "Maximum reached", description: "You can pick up to 10 interests." }); return; }
    setCustomInterests((p) => [v, ...p]);
    setSelectedInterests((p) => [...p, v]);
    setInterestSearch("");
  };

  const handlePickPicture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 5 * 1024 * 1024) { toast({ title: "Too large", description: "Max 5MB", variant: "destructive" }); return; }
    setUploadingPic(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `${user.id}/${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
      if (error) throw error;
      const { data } = supabase.storage.from("avatars").getPublicUrl(path);
      setProfilePic(data.publicUrl);
      await supabase.from("profiles").update({ profile_picture: data.publicUrl }).eq("user_id", user.id);
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message || "Try again", variant: "destructive" });
    } finally {
      setUploadingPic(false);
    }
  };

  const saveUsername = async () => {
    if (!user || username.trim().length < 3) return;
    await supabase.from("profiles").update({ username: username.trim() }).eq("user_id", user.id);
    next();
  };

  const saveProfile = async () => {
    if (!user || !age || parseInt(age) < 18 || !gender || !continent) return;
    await supabase.from("profiles").update({
      age: parseInt(age), gender, country: continent,
    }).eq("user_id", user.id);
    next();
  };

  const saveInterests = async () => {
    if (!user) return;
    await supabase.from("profiles").update({ interests: selectedInterests }).eq("user_id", user.id);
    next();
  };

  const saveSensitive = async () => {
    if (!user) return;
    await supabase.from("profiles").update({ sensitive_content: sensitive }).eq("user_id", user.id);
    next();
  };

  const finish = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      await completeSurvey({
        username: username.trim(),
        age: parseInt(age),
        country: continent,
        continent,
        gender,
      });
    } finally { setSubmitting(false); }
  };

  const Progress = () => (
    <div className="w-full flex items-center gap-1.5 mb-8">
      {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
        <div key={i} className={`flex-1 h-1.5 rounded-full transition-all ${i <= step ? "gradient-premium" : "bg-muted/30"}`} />
      ))}
    </div>
  );

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center px-6 py-8 overflow-y-auto relative">
      <div className="absolute top-10 -right-20 w-60 h-60 rounded-full bg-primary/[0.05] blur-[100px] pointer-events-none" />
      <div className="absolute bottom-10 -left-20 w-60 h-60 rounded-full bg-accent/[0.05] blur-[100px] pointer-events-none" />

      <div className="w-full max-w-sm relative z-10 flex-1 flex flex-col">
        <Progress />

        <AnimatePresence mode="wait">
          {/* STEP 0 — Welcome */}
          {step === 0 && (
            <motion.div key="welcome" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col items-center justify-center text-center">
              <img src={logoText} alt="TalkLot" className="h-16 mb-6 object-contain" style={{ mixBlendMode: "screen" }} />
              <h1 className="text-3xl font-display font-bold text-foreground mb-3">
                Welcome to <span className="text-gradient-premium">TalkLot</span>
              </h1>
              <p className="text-muted-foreground text-sm mb-10 max-w-xs">
                Meet new people, make real connections, and chat with the world — anytime, anywhere.
              </p>
              <motion.button whileTap={{ scale: 0.97 }} onClick={next}
                className="w-full py-3.5 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm flex items-center justify-center gap-2">
                Get Started <ArrowRight className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}

          {/* STEP 1 — Username */}
          {step === 1 && (
            <motion.div key="username" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col">
              <h1 className="text-2xl font-display font-bold text-foreground mb-2">Pick a username</h1>
              <p className="text-muted-foreground text-sm mb-6">This is how others will see you.</p>
              <input autoFocus type="text" placeholder="username" value={username} maxLength={20}
                onChange={(e) => setUsername(e.target.value.replace(/\s+/g, ""))}
                className="w-full px-4 py-3.5 rounded-xl bg-muted/30 border border-border/15 text-foreground placeholder:text-muted-foreground/50 text-sm focus:outline-none focus:ring-1 focus:ring-primary/40" />
              <p className="text-[11px] text-muted-foreground mt-2">3–20 characters, no spaces.</p>
              <div className="flex-1" />
              <div className="flex gap-2 mt-8">
                <button onClick={back} className="px-4 py-3 rounded-xl bg-muted/20 text-muted-foreground"><ArrowLeft className="w-4 h-4" /></button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={saveUsername} disabled={username.trim().length < 3}
                  className="flex-1 py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-30">
                  Continue
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* STEP 2 — Profile setup */}
          {step === 2 && (
            <motion.div key="profile" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col">
              <h1 className="text-2xl font-display font-bold text-foreground mb-2">Tell us about you</h1>
              <p className="text-muted-foreground text-sm mb-6">Helps us match you with the right people.</p>

              <div className="flex flex-col items-center mb-5">
                <button onClick={() => fileRef.current?.click()} disabled={uploadingPic}
                  className="relative w-24 h-24 rounded-full bg-muted/30 border border-border/20 flex items-center justify-center overflow-hidden">
                  {profilePic ? (
                    <img src={profilePic} alt="" className="w-full h-full object-cover" />
                  ) : uploadingPic ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : <Camera className="w-7 h-7 text-muted-foreground" />}
                  <div className="absolute bottom-0 right-0 w-7 h-7 rounded-full gradient-premium flex items-center justify-center border-2 border-background">
                    <Camera className="w-3.5 h-3.5 text-primary-foreground" />
                  </div>
                </button>
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePickPicture} />
                <p className="text-[11px] text-muted-foreground mt-2">Profile picture is optional</p>
              </div>

              <label className="text-xs text-muted-foreground mb-1.5 block">Age</label>
              <input type="number" min={18} max={99} placeholder="Your age" value={age} onChange={(e) => setAge(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40 mb-3" />
              {age && parseInt(age) < 18 && <p className="text-destructive text-[11px] -mt-2 mb-2">Must be 18+</p>}

              <label className="text-xs text-muted-foreground mb-1.5 block">Gender</label>
              <div className="grid grid-cols-3 gap-2 mb-4">
                {GENDERS.map(g => (
                  <button key={g} onClick={() => setGender(g)}
                    className={`py-2.5 rounded-xl text-xs font-medium ${gender === g ? "gradient-premium text-primary-foreground" : "bg-muted/30 text-muted-foreground border border-border/15"}`}>
                    {g}
                  </button>
                ))}
              </div>

              <label className="text-xs text-muted-foreground mb-1.5 block">Continent</label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                {CONTINENTS.map(c => (
                  <button key={c} onClick={() => setContinent(c)}
                    className={`py-2.5 px-3 rounded-xl text-xs font-medium text-left ${continent === c ? "gradient-premium text-primary-foreground" : "bg-muted/30 text-muted-foreground border border-border/15"}`}>
                    {c}
                  </button>
                ))}
              </div>

              <div className="flex gap-2 mt-6">
                <button onClick={back} className="px-4 py-3 rounded-xl bg-muted/20 text-muted-foreground"><ArrowLeft className="w-4 h-4" /></button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={saveProfile}
                  disabled={!age || parseInt(age) < 18 || !gender || !continent}
                  className="flex-1 py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-30">
                  Continue
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* STEP 3 — Interests */}
          {step === 3 && (
            <motion.div key="interests" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col">
              <div className="flex items-baseline justify-between mb-2">
                <h1 className="text-2xl font-display font-bold text-foreground">Your interests</h1>
                <span className="text-xs font-medium text-primary">{selectedInterests.length}/10</span>
              </div>
              <p className="text-muted-foreground text-sm mb-4">Pick up to 10. They'll show on your profile.</p>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                <input type="text" placeholder="Search or add your own…" value={interestSearch}
                  onChange={(e) => setInterestSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && canAddCustom) { e.preventDefault(); addCustom(); } }}
                  className="w-full pl-10 pr-4 py-3 rounded-xl bg-muted/30 border border-border/15 text-foreground text-sm focus:outline-none focus:ring-1 focus:ring-primary/40" />
              </div>

              {canAddCustom && (
                <button onClick={addCustom} className="mb-3 px-3 py-2 rounded-lg gradient-premium text-primary-foreground text-xs font-medium self-start">
                  + Add "{interestSearch.trim()}"
                </button>
              )}

              <div className="flex flex-wrap gap-2 pb-4">
                {filteredInterests.map(i => {
                  const sel = selectedInterests.includes(i);
                  return (
                    <button key={i} onClick={() => toggleInterest(i)}
                      className={`px-3.5 py-2 rounded-full text-xs font-medium transition-all ${sel ? "gradient-premium text-primary-foreground shadow-md" : "bg-muted/30 text-muted-foreground border border-border/15"}`}>
                      {sel && <Check className="inline w-3 h-3 mr-1" />}{i}
                    </button>
                  );
                })}
                {filteredInterests.length === 0 && !canAddCustom && (
                  <p className="text-xs text-muted-foreground py-4">No matches.</p>
                )}
              </div>

              <div className="flex gap-2 mt-auto pt-4 sticky bottom-0 bg-background/80 backdrop-blur-sm">
                <button onClick={back} className="px-4 py-3 rounded-xl bg-muted/20 text-muted-foreground"><ArrowLeft className="w-4 h-4" /></button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={saveInterests} disabled={selectedInterests.length === 0}
                  className="flex-1 py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm disabled:opacity-30">
                  Continue
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* STEP 4 — Sensitive content */}
          {step === 4 && (
            <motion.div key="sensitive" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
              className="flex-1 flex flex-col">
              <h1 className="text-2xl font-display font-bold text-foreground mb-2">Sensitive content</h1>
              <p className="text-muted-foreground text-sm mb-6">Decide what you want to see.</p>

              <div className="rounded-2xl bg-muted/20 border border-border/15 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-display font-semibold text-foreground text-sm">Show sensitive content</h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">May include mature themes, explicit language, or suggestive content.</p>
                  </div>
                  <button onClick={() => setSensitive(s => !s)}
                    className={`w-12 h-7 rounded-full p-0.5 transition-colors flex-shrink-0 ml-3 ${sensitive ? "gradient-premium" : "bg-muted/40"}`}>
                    <div className={`w-6 h-6 rounded-full bg-white shadow transition-transform ${sensitive ? "translate-x-5" : ""}`} />
                  </button>
                </div>
                <div className="text-[11px] text-muted-foreground bg-muted/20 rounded-lg p-3 mt-2">
                  ⚠️ You must be <span className="text-foreground font-medium">18 years or older</span> to enable this. By turning it on you confirm your age.
                </div>
              </div>

              <div className="flex gap-2 mt-auto pt-8">
                <button onClick={back} className="px-4 py-3 rounded-xl bg-muted/20 text-muted-foreground"><ArrowLeft className="w-4 h-4" /></button>
                <motion.button whileTap={{ scale: 0.97 }} onClick={saveSensitive}
                  className="flex-1 py-3 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm">
                  Continue
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* STEP 5 — All set */}
          {step === 5 && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
              className="flex-1 flex flex-col items-center justify-center text-center">
              <motion.div initial={{ scale: 0, rotate: -45 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
                className="w-24 h-24 rounded-full gradient-premium flex items-center justify-center mb-6 relative">
                <PartyPopper className="w-12 h-12 text-primary-foreground" />
                {[...Array(8)].map((_, i) => (
                  <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-primary"
                    initial={{ x: 0, y: 0, opacity: 1 }}
                    animate={{ x: Math.cos(i * Math.PI / 4) * 80, y: Math.sin(i * Math.PI / 4) * 80, opacity: 0 }}
                    transition={{ duration: 1.2, repeat: Infinity, repeatDelay: 0.4, delay: i * 0.05 }} />
                ))}
              </motion.div>
              <h1 className="text-3xl font-display font-bold text-foreground mb-3">You're all set!</h1>
              <p className="text-muted-foreground text-sm mb-10 max-w-xs">
                Welcome to TalkLot, {username}. Time to meet new people.
              </p>
              <motion.button whileTap={{ scale: 0.97 }} onClick={finish} disabled={submitting}
                className="w-full py-3.5 rounded-xl gradient-premium text-primary-foreground font-display font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                <Sparkles className="w-4 h-4" /> Enter TalkLot
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SurveyPage;
